<template>
    <div class="user-permissions-grid-container">
        <promo-ag-grid
            :row-data="rowData"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :grid-options="gridOptions"
            grid-style="width: 100%; height: 100%;"
            dom-layout="normal"
        />
        <checkbox-list-dialog ref="hierarchy-dialog" :user="selectedUser" />
        <store-group-attributes-dialog ref="store-group-dialog" :user="selectedUser" />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { get, has, isNil, uniq, isArray } from 'lodash';
import userRoles from '@enums/user-roles';

import featureFlags from '@enums/feature-flags';
import resources from '@enums/resources';
import agGridUtils from '@/js/utils/ag-grid-utils';
import platformRoles from '@/js/utils/platform-roles-utils';
import EditIconRenderer from './components/edit-icon-renderer.vue';
import DeleteIconRenderer from './components/delete-icon-renderer.vue';
import CheckboxRenderer from './components/checkbox-renderer.vue';

export default {
    /* eslint-disable vue/no-unused-components */
    components: {
        EditIconRenderer,
        DeleteIconRenderer,
        CheckboxRenderer,
    },
    data() {
        return {
            defaultColDef: {
                editable: false, // read only for now, make-read-only on component too
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                sortable: true, // All columns default to being sortable.
                unSortIcon: true, // Ensures the sort icon displays all the time (not just when hovered over).
                flex: 1,
                resizable: true,
                filter: true,
                minWidth: 120,
                headerComponentParams: {},
                menuTabs: ['filterMenuTab'],
            },
            gridOptions: markRaw({
                headerHeight: 48,
                rowHeight: 35, // Specified in pixels.
                columnTypes: {
                    numericColumnCustom: agGridUtils.columnTypes.numericColumnCustom,
                },
                cellSelection: true,
                ensureDomOrder: true,
                processCellForClipboard: null,
            }),
            selectedUser: null,
            columnDefs: null,
        };
    },

    computed: {
        ...mapState('userProfiles', ['userProfiles']),
        ...mapState('clientConfig', ['toggleLogic']),
        ...mapGetters('hierarchy', ['getHierarchyByKey']),
        ...mapGetters('context', ['getCurrentTenant']),

        users() {
            // all users apart from the ADMIN
            return this.userProfiles.filter(
                user => !this.tenantRoles(user).includes(userRoles.admin)
            );
        },

        rowData() {
            return this.users.map(u => {
                const roles = this.tenantRoles(u);
                u.isAdmin = roles.some(role =>
                    role.toLowerCase().includes(userRoles.admin.toLowerCase())
                );
                const attributes = uniq(
                    get(u, 'accessRestrictions.storeGroupAttributeRestrictions', []).reduce(
                        (acc, row) => {
                            return [
                                ...acc,
                                ...row.reduce((innerAcc, attr) => {
                                    if (attr.values && attr.values.length) {
                                        return [...innerAcc, ...attr.values];
                                    }
                                    return innerAcc;
                                }, []),
                            ];
                        },
                        []
                    )
                );
                if (attributes.length) {
                    u.attributes = attributes;
                } else {
                    u.attributes = null;
                }

                u.rolesString = roles.join(', ');
                if (!has(u, 'access')) {
                    u.access = { categories: [], storeGroups: [] };
                }
                if (!has(u, 'accessRestrictions')) {
                    u.accessRestrictions = { storeGroupAttributeRestrictions: [] };
                }
                return u;
            });
        },
    },

    created() {
        this.columnDefs = [
            {
                headerName: this.toSentenceCase(this.$t('userPermissions.headings.username')),
                field: 'username',
            },
            {
                headerName: this.toSentenceCase(this.$t('userPermissions.headings.role')),
                field: 'rolesString',
                minWidth: 300,
            },
            // Access
            ...(this.toggleLogic[featureFlags.restrictAccessByCategories]
                ? [
                      {
                          minWidth: 50,
                          sortable: false,
                          field: 'access',
                          headerName: this.toSentenceCase(
                              this.$t('userPermissions.headings.hierarchyAccess')
                          ),
                          valueGetter: ({ data }) => {
                              const cat = (data.access || {}).categories;
                              if (cat && cat.length) {
                                  return cat;
                              }

                              return null;
                          },
                          valueFormatter: ({ value }) => {
                              if (isNil(value) || !isArray(value)) {
                                  return '';
                              }

                              const descriptions = value
                                  .map(valueItem => this.getHierarchyByKey(valueItem))
                                  .map(hierarchy =>
                                      isNil(hierarchy) ? '' : hierarchy.levelEntryDescription
                                  )
                                  .filter(Boolean);

                              return descriptions.join(', ');
                          },
                          filterParams: {
                              valueFormatter: ({ value }) => {
                                  if (isNil(value)) {
                                      return value;
                                  }
                                  const hierarchy = this.getHierarchyByKey(value);

                                  return isNil(hierarchy) ? null : hierarchy.levelEntryDescription;
                              },
                          },
                          cellRenderer: 'EditIconRenderer',
                          cellRendererParams: {
                              onClick: params => {
                                  this.openHierarchyDialog(params);
                              },
                          },
                      },
                  ]
                : []),
            ...(this.toggleLogic[featureFlags.restrictAccessByStoreGroups]
                ? [
                      {
                          minWidth: 50,
                          sortable: false,
                          headerName: this.toSentenceCase(
                              this.$t('userPermissions.headings.storeGroupAccess')
                          ),
                          field: 'attributes',
                          valueFormatter: ({ value }) => {
                              if (isNil(value)) {
                                  return '';
                              }

                              return isArray(value) ? value.join(', ') : String(value);
                          },
                          cellRenderer: 'EditIconRenderer',
                          cellRendererParams: {
                              onClick: params => {
                                  this.openStoreGroupDialog(params);
                              },
                          },
                      },
                  ]
                : []),
            // isAdmin
            {
                headerName: this.toSentenceCase(this.$t('userPermissions.headings.admin')),
                field: 'isAdmin',
                cellClass: ['cell-padding'],
                filterParams: {
                    valueFormatter: ({ value }) => {
                        return value === 'true'
                            ? this.$t('userPermissions.yes')
                            : this.$t('userPermissions.no');
                    },
                },
                cellRenderer: 'CheckboxRenderer',
            },
            ...(this.toggleLogic[featureFlags.userProfilesDeletePermission]
                ? [
                      {
                          minWidth: 50,
                          sortable: false,
                          headerName: this.toSentenceCase(this.$t('actions.remove')),
                          cellRenderer: 'DeleteIconRenderer',
                          cellRendererParams: {
                              resource: resources.userProfiles,
                              resourceId: '_id',
                          },
                      },
                  ]
                : []),
        ];
        this.gridOptions.processCellForClipboard = this.processCellForClipboardFunction;
    },

    methods: {
        // Stored roles are namespaced per app and tenant - ADR-016 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/016-authorization-responsibility-split.md
        // This screen shows one tenant at a time, so every role predicate here answers for the
        // tenant on screen.
        tenantRoles(user) {
            return platformRoles.resolveRolesForTenant(
                get(user, 'roles', []),
                this.getCurrentTenant
            );
        },
        processCellForClipboardFunction(params) {
            if (params.column.getColId() === 'access') {
                const { value } = params;
                if (isNil(value) || !isArray(value)) {
                    return value;
                }
                const hierarchyList = value.map(valueItem => this.getHierarchyByKey(valueItem));
                return (hierarchyList || []).map(hierarchy =>
                    isNil(hierarchy) ? '' : hierarchy.levelEntryDescription
                );
            }
            return params.value;
        },
        openHierarchyDialog(user = {}) {
            this.selectedUser = user;
            this.$nextTick(() => {
                this.$refs['hierarchy-dialog'].openDialog();
            });
        },
        openStoreGroupDialog(user = {}) {
            this.selectedUser = user;
            this.$nextTick(() => {
                this.$refs['store-group-dialog'].openDialog();
            });
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.user-permissions-grid-container {
    height: calc(100% - #{$main-footer-height});
    width: 100%;
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    :deep(.cell-padding) {
        .ag-cell-value {
            padding-left: 0.3rem;
        }
    }
}
</style>
